import { makeObservable, observable, action, reaction } from 'mobx';
import { Disposer } from '../../custom_shared/misc';
import { TypeUtils } from '../../common/misc';
import { ActionParams } from '../types';
import { CustomSessionRuntimeData } from '../../custom_shared/types';
import { NodeBaseModel, LayoutSettingsBaseModel } from '../models';
import AppStore from './AppStore';
import SessionStore from './SessionStore';
import NodesStore from './NodesStore';
import SettingsStore from './SettingsStore';

export const Actions = {
    ChangeBusinessType: 'ChangeBusinessType',
    AddLimit: 'AddLimit',
    AddDeduction: 'AddDeduction',
    RebuildNodes: 'RebuildNodes',
    SendContract: 'SendContract',
    ExportToJson: 'ExportToJson',
    ClearPostAction: 'ClearPostAction',
    InitializeContractIngestion: 'InitializeContractIngestion'
} as const;

export const ActionParamKeys = {
    NodeId: 'NodeId',
    LayerId: 'LayerId',
    SectionId: 'SectionId'
} as const;

export default class ActionStore extends Disposer {
    static RuntimeDataKeys = {
        ActionParams: 'ActionParams',
        LayoutSettings: 'LayoutSettings'
    };

    downloadUrl: string | null = null;

    constructor(
        private readonly appStore: AppStore,
        private readonly sessionStore: SessionStore,
        private readonly nodesStore: NodesStore,
        private readonly settingStore: SettingsStore
    ) {
        super();

        makeObservable(this, {
            downloadUrl: observable,
            setDownloadUrl: action
        });

        this.reactions.push(
            reaction(
                () => this.sessionStore.response,
                () => {
                    if (!this.sessionStore.response || this.sessionStore.inProgress) {
                        return;
                    }

                    const postAction = this.sessionStore.response.postAction;
                    const downloadAction = postAction && postAction.type === 'Download' ? postAction : null;

                    if (!downloadAction) {
                        return this.setDownloadUrl(null);
                    }

                    const downloadUrl = TypeUtils.isString(downloadAction.metadata.url)
                        ? downloadAction.metadata.url
                        : null;

                    this.setDownloadUrl(downloadUrl);
                }
            )
        );

        this.reactions.push(
            reaction(
                () => this.downloadUrl,
                () => {
                    if (!this.downloadUrl) {
                        return;
                    }

                    const downloadMessage = `Do you want to download file produced after executing '${this.appStore.applicationData.appName}' on '${this.appStore.packageName}'?`;

                    if (confirm(downloadMessage)) {
                        this.sessionStore.getFileFromIotaTempStorage(this.downloadUrl);
                        this.clearPostAction();
                    } else {
                        this.clearPostAction();
                    }
                }
            )
        );
    }

    static getRuntimeData(
        nodes: NodeBaseModel[],
        layoutSettings: LayoutSettingsBaseModel | null,
        actionParams: Record<string, unknown> = {}
    ): CustomSessionRuntimeData {
        const nodesRuntimeData = nodes.reduce<CustomSessionRuntimeData>((acc, node) => {
            acc[node.id] = { value: JSON.stringify(node.dto) };
            return acc;
        }, {});

        const additionalRuntimeData = {
            [ActionStore.RuntimeDataKeys.ActionParams]: {
                value: actionParams
            }
        };

        const settings = {
            [ActionStore.RuntimeDataKeys.LayoutSettings]: {
                value: layoutSettings ? JSON.stringify(layoutSettings.dto) : null
            }
        };

        return {
            ...nodesRuntimeData,
            ...additionalRuntimeData,
            ...settings
        };
    }

    setDownloadUrl(downloadUrl: string | null) {
        this.downloadUrl = downloadUrl;
    }

    async changeBusinessType() {
        await this.executeAction(Actions.ChangeBusinessType);
    }

    async addLimit(actionParams: ActionParams = {}) {
        await this.executeAction(Actions.AddLimit, actionParams);
    }

    async addDeduction(actionParams: ActionParams = {}) {
        await this.executeAction(Actions.AddDeduction, actionParams);
    }

    async initializeContractIngestion() {
        await this.executeAction(Actions.InitializeContractIngestion);
    }

    async rebuildNodes() {
        await this.executeAction(Actions.RebuildNodes);
    }

    async sendContract() {
        await this.executeAction(Actions.SendContract);
    }

    async exportToJson() {
        await this.executeAction(Actions.ExportToJson);
    }

    async clearPostAction() {
        await this.executeAction(Actions.ClearPostAction);
    }

    dispose() {
        this.disposeReactions();
    }

    private async executeAction(actionId: string, actionParams: ActionParams = {}): Promise<void> {
        await this.sessionStore.continueSession(
            actionId,
            ActionStore.getRuntimeData(this.nodesStore.nodes, this.settingStore.layoutSettings, actionParams)
        );
    }
}
